<template>
  <div class="bg-white background">
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      絞込条件
      <template v-slot:right>
        <div class="reset-button text-size-14" @click="resetConditions">
          すべて解除
        </div>
      </template>
    </Header>

    <div class="pt-4 px-3">
      <div
        class="search-conditions"
        :class="{ fix: isOpenedServiceTypeFilter }"
      >
        <div>
          <h1 class="condition-headline">勤務地</h1>
          <div class="section">
            <div>
              <div class="input-label">
                都道府県
              </div>
              <button
                class="condition-button d-flex align-items-center justify-content-between w-100"
                @click="isOpenedPrefectureFilter = true"
              >
                <span class="condition-button__text flex-grow-1">{{
                  locationText
                }}</span>
                <span
                  v-if="selectedPrefectures.length > 0"
                  class="ml-2 text-center condition-button__count"
                  >({{ selectedPrefectures.length }})</span
                >
                <FeatherIcon
                  icon="chevron-right"
                  class="condition-button__icon"
                  size="1x"
                />
              </button>
            </div>
            <div>
              <div class="input-label">
                市区町村
              </div>
              <!-- ワーカーに市区町村でも絞り込みできるということを示すため、モックボタンを表示する -->
              <button
                v-if="sortedLocalityOptions.length === 0"
                class="locality-mock-button"
                data-testid="locality-mock-button"
                disabled
              >
                指定なし
              </button>
              <LocalityConditionForm
                v-else
                v-for="option in sortedLocalityOptions"
                :key="option.prefecture.id"
                :option="option"
                v-model="inputValue.localities"
                data-testid="locality-button"
              />
            </div>
          </div>
        </div>

        <div v-if="isCaitechCareProduct">
          <h1 class="condition-headline">お仕事</h1>
          <div class="section">
            <FormCheckboxGroup
              label="時間帯"
              name="shiftTypes"
              v-model="inputValue.shiftTypes"
              text-field="name"
              value-field="id"
              :options="shiftTypeOptions"
              class="text-size-12 work-time-checkbox"
            />

            <div class="d-flex flex-column">
              <label class="input-label">タイプ</label>
              <AppCheckbox
                v-if="isLogin && isNursing"
                v-model="inputValue.isCheckedOnlyNursing"
                id="nursing"
                text="看護のお仕事のみ"
                class="checkbox mb-1"
              />
              <AppCheckbox
                v-model="inputValue.isCheckedExcludeCareer"
                id="exclude-career"
                text="説明会求人を除く"
                class="checkbox"
              />
            </div>

            <div class="d-flex flex-column">
              <label class="input-label">資格条件</label>
              <AppCheckbox
                v-model="inputValue.isCheckedCanApply"
                @input="onChangeCanApply"
                :class="{ disabled: !isLogin }"
                id="can-apply"
                text="登録した資格で応募できる仕事のみ"
                class="checkbox"
              />
            </div>
          </div>
        </div>

        <!-- 詳細条件 -->
        <details>
          <summary class="condition-headline additional-condition-summary"
            >更に詳細な条件で絞り込む
            <span
              v-if="countDetailsCondition > 0"
              class="count-details-condition"
              data-testid="count-details-condition"
              >{{ countDetailsCondition }}</span
            >
            <FeatherIcon
              icon="chevron-right"
              class="condition-button__icon additional-condition-summary-icon"
              size="1x"
            />
          </summary>
          <div class="section">
            <div>
              <label class="input-label">サービス種別</label>
              <button class="condition-button" @click="onClickServiceType">
                <span class="condition-button__text">{{
                  serviceTypeText
                }}</span>
                <FeatherIcon
                  icon="chevron-right"
                  class="condition-button__icon"
                  size="1x"
                />
              </button>
            </div>

            <div class="d-flex flex-column">
              <label class="input-label">通勤手段</label>
              <AppCheckbox
                id="canCommuteByCar"
                name="canCommuteByCar"
                v-model="inputValue.canCommuteByCar"
                text="車通勤OK"
                class="checkbox"
              />
            </div>

            <div class="mb-1">
              <label class="input-label">お仕事の勤務時間</label>
              <BRow class="mx-n2">
                <BCol class="px-2">
                  <AppFormSelect
                    v-model="inputValue.beginHour"
                    :formOptions="beginHourOptions"
                  />
                </BCol>
                <BCol cols="auto" class="px-0">
                  <p class="mt-2 text-center">〜</p>
                </BCol>
                <BCol class="px-2">
                  <AppFormSelect
                    v-model="inputValue.endHour"
                    :formOptions="endHourOptions"
                  />
                </BCol>
              </BRow>
            </div>

            <FormCheckboxGroup
              label="お仕事の長さ"
              name="durationTypes"
              v-model="inputValue.durationTypes"
              text-field="name"
              value-field="id"
              :options="durationTypeOptions"
              class="work-time-checkbox"
            />

            <div>
              <label class="input-label">時給</label>
              <AppFormSelect
                v-model="inputValue.hourlyWage"
                :formOptions="hourlyWageOptions"
              />
            </div>

            <div v-if="isCaitechCareProduct">
              <label class="input-label">仕事内容</label>
              <div class="operations">
                <div class="operations-checkbox">
                  <AppCheckbox
                    id="withoutBathAssistance"
                    text="入浴介助なし"
                    v-model="inputValue.withoutBathAssistance"
                    class="operations-checkbox checkbox"
                  />
                </div>
                <div class="operations-checkbox">
                  <AppCheckbox
                    id="withShuttleDriverDuties"
                    text="送迎ドライバー"
                    v-model="inputValue.withShuttleDriverDuties"
                    class="checkbox"
                  />
                </div>
              </div>
            </div>

            <FormCheckboxGroup
              label="経験・スキル"
              name="recommendationPointsExperienceAndSkills"
              v-model="inputValue.recommendationPoints"
              :options="experienceAndSkillsOptions"
              class="work-time-checkbox"
            />

            <FormCheckboxGroup
              label="服装・身だしなみ"
              name="recommendationPointsAppearance"
              v-model="inputValue.recommendationPoints"
              :options="appearanceOptions"
              class="work-time-checkbox"
            />

            <div class="d-flex flex-column">
              <label class="input-label">その他</label>
              <AppCheckbox
                id="serviceStartedWithinLastFiveYears"
                name="serviceStartedWithinLastFiveYears"
                v-model="inputValue.serviceStartedWithinLastFiveYears"
                text="施設オープン5年以内"
                class="checkbox"
              />
            </div>

            <div>
              <label class="input-label"
                >フリーワード<span class="ml-2 text-size-10 text-gray-05"
                  >仕事詳細などから検索できます</span
                ></label
              >
              <BFormInput
                v-model="inputValue.freeWord"
                class="freeword"
                ref="freewordInput"
              />
            </div>
          </div>
        </details>
      </div>
      <div class="fixed-button">
        <AppButton block @click="search">
          検索
        </AppButton>
      </div>
    </div>

    <!-- 各種検索Modal -->
    <SearchModal
      v-if="isOpenedPrefectureFilter"
      title="都道府県"
      @clickBack="isOpenedPrefectureFilter = false"
      @save="savePrefectureCondition"
    >
      <FormCheckboxGroup
        name="prefectures"
        v-model="inputValue.prefectures"
        :options="prefectureOptions"
        text-field="name"
        value-field="id"
      />
    </SearchModal>
    <SearchModal
      v-if="isOpenedServiceTypeFilter"
      title="サービス種別"
      @clickBack="isOpenedServiceTypeFilter = false"
      @save="saveServiceTypeCondition"
    >
      <FormCheckboxGroup
        name="serviceTypes"
        v-model="inputValue.serviceTypes"
        :options="serviceTypeOptions"
      />
    </SearchModal>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import Header from "@/components/organisms/common/Header.vue";
import LocalityConditionForm from "@/components/organisms/orders/LocalityConditionForm.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import FormCheckboxGroup from "@/components/molecules/form/FormCheckboxGroup.vue";
import AppCheckbox from "@/components/atoms/input/AppCheckbox.vue";
import AppTextField from "@/components/atoms/input/AppTextField.vue";
import AppFormSelect from "@/components/atoms/input/AppFormSelect.vue";
import { localitiesService } from "@/app/services/LocalitiesService";
import { accountService } from "@/app/services/AccountService";
import { masterStore } from "@/store/master";
import { orderStore } from "@/store/order";
import { authStore } from "@/store/auth";
import SearchModal from "@/components/organisms/orders/SearchModal.vue";
import { Prefecture } from "@/types/Master";
import { RecommendationPoint } from "@/types/order/RecommendationPoint";
import { LocalityOption } from "@/types/form/LocalityOption";
import { strip } from "@/utils/stringUtil";
import {
  isToday,
  startOfToday,
  parseISO,
  formatFromUnixTimestamp
} from "@/utils/date";
import { isNotNullOrUndefined } from "@/utils/nullUndefinedCheck";
import { formatNumber } from "@/utils/number";

type InputValue = {
  prefectures: string[];
  localities: string[];
  isCheckedOnlyNursing: boolean;
  isCheckedExcludeCareer: boolean;
  isCheckedCanApply: boolean;
  qualifications: string[];
  serviceTypes: string[];
  shiftTypes: string[];
  durationTypes: string[];
  beginHour: number | null;
  endHour: number | null;
  freeWord: string;
  hourlyWage: number | null;
  recommendationPoints: string[];
  withoutBathAssistance: boolean;
  withShuttleDriverDuties: boolean;
  canCommuteByCar: boolean;
  serviceStartedWithinLastFiveYears: boolean;
};

@Component({
  components: {
    Header,
    AppButton,
    FeatherIcon,
    FormCheckboxGroup,
    LocalityConditionForm,
    AppCheckbox,
    AppTextField,
    AppFormSelect,
    SearchModal
  }
})
export default class SearchCondition extends Vue {
  shiftTypeOptions = [
    { id: "DAY", name: "日勤" },
    { id: "NIGHT", name: "夜勤" }
  ];
  durationTypeOptions = [
    { id: "SHORT", name: "4時間以下" },
    { id: "NON_SHORT", name: "4時間超" }
  ];
  prefectures: Prefecture[] = [];
  localityOptions: LocalityOption[] = [];
  myQualifications: string[] = [];
  recommendationPoints: RecommendationPoint[] = [];
  isOpenedPrefectureFilter = false;
  isOpenedServiceTypeFilter = false;
  inputValue: InputValue = {
    freeWord: "",
    prefectures: [],
    localities: [],
    qualifications: [],
    serviceTypes: [],
    shiftTypes: [],
    durationTypes: [],
    beginHour: null,
    endHour: null,
    hourlyWage: null,
    isCheckedCanApply: false,
    isCheckedOnlyNursing: false,
    isCheckedExcludeCareer: false,
    recommendationPoints: [],
    withoutBathAssistance: false,
    withShuttleDriverDuties: false,
    canCommuteByCar: false,
    serviceStartedWithinLastFiveYears: false
  };
  // 検索条件
  selectedDates: string[] = [];
  selectedPrefectures: string[] = [];
  selectedQualifications: string[] = [];
  selectedServiceTypes: string[] = [];

  get isLogin() {
    return authStore.existToken;
  }
  get isNursing() {
    return authStore.isNursing;
  }
  get isCaitechCareProduct() {
    return authStore.isCaitechCareProduct;
  }
  get locationText() {
    if (this.selectedPrefectures.length === 0) return "指定なし";
    const selectedOptions = this.prefectures.filter(prefecture => {
      return this.selectedPrefectures.includes(prefecture.id.toString());
    });
    return selectedOptions.map(option => option.prefecture).join(", ");
  }
  get serviceTypeText() {
    if (this.selectedServiceTypes.length === 0) return "すべて";
    const selectedOptions = this.serviceTypeOptions.filter(option => {
      return this.selectedServiceTypes.includes(option.value);
    });
    return selectedOptions.map(option => option.text).join(", ");
  }
  get selectedDateIdsJoinnedComma() {
    if (this.selectedDates.length === 0) return null;
    return this.selectedDates.join(",");
  }
  get selectedPrefecturesJoinnedComma() {
    if (this.selectedPrefectures.length === 0) return null;
    return this.selectedPrefectures.join(",");
  }
  get selectedLocalitiesJoinnedComma() {
    if (this.inputValue.localities.length === 0) return null;
    return this.inputValue.localities.join(",");
  }
  get selectedQualificationsJoinnedComma() {
    if (this.selectedQualifications.length === 0) return null;
    return this.selectedQualifications.join(",");
  }
  get selectedServiceTypesJoinnedComma() {
    if (this.selectedServiceTypes.length === 0) return null;
    return this.selectedServiceTypes.join(",");
  }
  get selectedShiftTypesJoinnedComma() {
    if (this.inputValue.shiftTypes.length === 0) return null;
    return this.inputValue.shiftTypes.join(",");
  }
  get selectedDurationTypeJoinnedComma() {
    if (this.inputValue.durationTypes.length === 0) return null;
    return this.inputValue.durationTypes.join(",");
  }
  get checkedCanApply() {
    return this.inputValue.isCheckedCanApply ? "1" : null;
  }
  get freeWord() {
    return strip(this.inputValue.freeWord) || null;
  }
  get recommendationPointsJoinnedComma() {
    if (this.inputValue.recommendationPoints.length === 0) return null;
    return this.inputValue.recommendationPoints.join(",");
  }
  get checkedOnlyNursing() {
    // 業務カテゴリ「看護」以外の場合、条件は設定しない
    if (!this.isNursing) {
      return null;
    }
    return this.inputValue.isCheckedOnlyNursing ? "1" : null;
  }
  get sortedLocalityOptions() {
    const options = this.localityOptions;
    return options.sort(
      (a, b) => Number(a.prefecture.id) - Number(b.prefecture.id)
    );
  }
  get prefectureOptions() {
    return this.prefectures.map(prefecture => ({
      id: prefecture.id.toString(),
      name: prefecture.prefecture
    }));
  }
  get serviceTypeOptions() {
    const services = masterStore.servicesForOffice;
    return services.map(service => ({
      text: service.name,
      value: String(service.id)
    }));
  }

  get workerServiceIdsByProduct() {
    return authStore.product.workerServices.map(ws => ws.id);
  }

  get experienceAndSkillsOptions() {
    return masterStore.recommendationPoints
      .filter(point => point.category === "experience")
      .filter(point =>
        // プロダクトが取り扱える業務カテゴリに絞り込む
        point.workerServiceIds.some(workerServiceId =>
          this.workerServiceIdsByProduct.includes(workerServiceId)
        )
      )
      .map(point => ({
        text: point.name,
        value: String(point.id)
      }));
  }

  get appearanceOptions() {
    return masterStore.recommendationPoints
      .filter(point => point.category === "appearance")
      .filter(point =>
        // プロダクトが取り扱える業務カテゴリに絞り込む
        point.workerServiceIds.some(workerServiceId =>
          this.workerServiceIdsByProduct.includes(workerServiceId)
        )
      )
      .map(point => ({
        text: point.name,
        value: String(point.id)
      }));
  }

  get countDetailsCondition() {
    return (
      this.inputValue.serviceTypes.length +
      this.inputValue.durationTypes.length +
      (this.inputValue.freeWord.length > 0 ? 1 : 0) +
      (isNotNullOrUndefined(this.inputValue.beginHour) ||
      isNotNullOrUndefined(this.inputValue.endHour)
        ? 1
        : 0) +
      this.inputValue.recommendationPoints.length +
      (this.inputValue.withoutBathAssistance ? 1 : 0) +
      (this.inputValue.withShuttleDriverDuties ? 1 : 0) +
      (this.inputValue.serviceStartedWithinLastFiveYears ? 1 : 0) +
      (this.inputValue.hourlyWage ? 1 : 0) +
      (this.inputValue.canCommuteByCar ? 1 : 0)
    );
  }

  /**
   * 就業時間帯の勤務開始時刻の選択肢を生成する
   * 00:00~23:00までの選択肢を生成する
   */
  get beginHourOptions() {
    return [
      { text: "指定なし", value: null }, // 未選択選択肢
      ...Array.from({ length: 24 }).map((_, hour) => {
        const hourStr = String(hour).padStart(2, "0");
        return {
          text: `${hourStr}:00`,
          value: hour
        };
      })
    ];
  }
  /**
   * 就業時間帯の勤務終了時刻の選択肢を生成する
   * 01:00~翌12:00までの選択肢を生成する
   */
  get endHourOptions() {
    return [
      { text: "指定なし", value: null }, // 未選択選択肢
      ...Array.from({ length: 36 }).map((_, index) => {
        const hour = index + 1;
        const upwardHour = hour < 24 ? hour : hour - 24;
        const upwardHourStr = String(upwardHour).padStart(2, "0");
        return {
          text: hour < 24 ? `${upwardHourStr}:00` : `翌${upwardHourStr}:00`,
          value: hour
        };
      })
    ];
  }

  /**
   * 時給の選択肢を生成する
   * カイテク（介護・看護）: 800円~3,000円以上までの選択肢を生成する
   * カイテク薬剤師: 2,000円~3,500円以上までの選択肢を生成する
   */
  get hourlyWageOptions() {
    const minHourlyWageIndex = this.isCaitechCareProduct ? 8 : 20;
    const rangeLength = this.isCaitechCareProduct ? 23 : 16;
    const options = [{ text: "指定なし", value: null }];
    return [
      ...options,
      ...Array.from({ length: rangeLength }).map((_, index) => {
        const wage = (index + minHourlyWageIndex) * 100;
        return {
          text: `${formatNumber(wage)}円以上`,
          value: wage
        };
      })
    ];
  }

  async created() {
    await this.fetchMyQualification();

    this.initSearchQuery();
    this.fetchPrefectureOptions();

    await Promise.all(
      this.selectedPrefectures.map(async prefecture => {
        await this.fetchLocalityOptions(prefecture);
      })
    );
  }
  onClickBack() {
    this.$router.back();
  }

  onClickServiceType() {
    this.isOpenedServiceTypeFilter = true;
    (this.$refs.freewordInput as HTMLInputElement).blur();
  }
  resetConditions() {
    this.selectedPrefectures = [];
    this.selectedQualifications = [];
    this.selectedServiceTypes = [];
    this.localityOptions = [];

    this.inputValue = {
      freeWord: "",
      prefectures: [],
      localities: [],
      qualifications: [],
      serviceTypes: [],
      shiftTypes: [],
      durationTypes: [],
      beginHour: null,
      endHour: null,
      isCheckedCanApply: false,
      isCheckedOnlyNursing: false,
      isCheckedExcludeCareer: false,
      hourlyWage: null,
      recommendationPoints: [],
      withoutBathAssistance: false,
      withShuttleDriverDuties: false,
      canCommuteByCar: false,
      serviceStartedWithinLastFiveYears: false
    };
  }

  async search() {
    if (
      this.inputValue.beginHour &&
      this.inputValue.endHour &&
      this.inputValue.beginHour >= this.inputValue.endHour
    ) {
      await this.$bvModal.msgBoxOk("終了時間は開始時間より後を指定して下さい", {
        centered: true
      });
      return;
    }

    // store保存
    orderStore.setSearchQuery({
      dates: this.selectedDateIdsJoinnedComma,
      localities: this.selectedLocalitiesJoinnedComma,
      prefectures: this.selectedPrefecturesJoinnedComma,
      can_apply: this.checkedCanApply,
      qualifications: this.selectedQualificationsJoinnedComma,
      service_types: this.selectedServiceTypesJoinnedComma,
      shift_types: this.selectedShiftTypesJoinnedComma,
      duration_types: this.selectedDurationTypeJoinnedComma,
      only_nursing: this.checkedOnlyNursing,
      exclude_career: this.inputValue.isCheckedExcludeCareer,
      begin_hour: this.inputValue.beginHour,
      end_hour: this.inputValue.endHour,
      free_word: this.freeWord,
      hourly_wage: this.inputValue.hourlyWage,
      recommendation_points: this.recommendationPointsJoinnedComma,
      without_bath_assistance: this.inputValue.withoutBathAssistance,
      with_shuttle_driver_duties: this.inputValue.withShuttleDriverDuties,
      can_commute_by_car: this.inputValue.canCommuteByCar,
      service_started_within_last_five_years: this.inputValue
        .serviceStartedWithinLastFiveYears
    });

    this.$router.back();
  }
  async fetchMyQualification() {
    if (!this.isLogin) return;
    const { results } = await accountService.fetchQualifications();
    this.myQualifications = results.map(item => {
      return String(item.qualification);
    });
  }
  async fetchLocalityOptions(prefectureId: string) {
    const localities = await localitiesService.fetchLocalitiesWithSeirei(
      Number(prefectureId)
    );
    const mappedLocalities = localities
      .filter(locality => !locality.isSeirei)
      .map(locality => ({
        value: String(locality.id),
        text: locality.city
      }));
    const prefecture = this.prefectures.find(
      option => option.id.toString() === prefectureId
    );
    if (!prefecture) return;
    this.localityOptions.push({
      prefecture: {
        id: prefecture.id.toString(),
        name: prefecture.prefecture
      },
      localities: mappedLocalities
    });
  }
  fetchPrefectureOptions() {
    this.prefectures = masterStore.prefectures.filter(prefecture => {
      if (authStore.isCaitechCareProduct) {
        return prefecture.isCaitechTarget;
      }
      if (authStore.isCaitechPharmacistProduct) {
        return prefecture.isCaitechPharmacistTarget;
      }
      return true;
    });
  }
  initSearchQuery() {
    const today = startOfToday();
    const orderSearchQuery = orderStore.orderSearchQuery;

    // storeがnullの場合、今日の案件を取得
    if (!orderSearchQuery) {
      const dates = [formatFromUnixTimestamp(today, "yyyy-MM-dd")];
      this.selectedDates = dates;

      orderStore.setSearchQuery({
        dates: formatFromUnixTimestamp(today, "yyyy-MM-dd")
      });
      return;
    }

    if (orderSearchQuery.dates) {
      // フォーマット=yyyy-mm-dd
      const dates = orderSearchQuery.dates.split(",").map(date => {
        // 今日より前ならnullを返す
        const dateObject = parseISO(date);
        if (!isToday(dateObject) && dateObject < today) {
          return null;
        }
        return date;
      });
      // nullを削除
      const filteredDates = dates.filter(
        (v): v is NonNullable<typeof v> => v !== null
      );
      // 配列の数がゼロなら今日のデータを入れる
      if (filteredDates.length === 0) {
        filteredDates.push(formatFromUnixTimestamp(today, "yyyy-MM-dd"));
      }
      this.selectedDates = filteredDates;
    }

    if (orderSearchQuery.prefectures) {
      const prefectures = orderSearchQuery.prefectures.split(",");
      this.selectedPrefectures = prefectures;
      this.inputValue.prefectures = prefectures;
    }
    if (orderSearchQuery.can_apply) {
      this.inputValue.isCheckedCanApply = true;
    }
    if (orderSearchQuery.localities) {
      const localities = orderSearchQuery.localities.split(",");
      this.inputValue.localities = localities;
    }
    if (
      orderSearchQuery.begin_hour !== null &&
      orderSearchQuery.begin_hour !== undefined
    ) {
      this.inputValue.beginHour = orderSearchQuery.begin_hour;
    }
    if (orderSearchQuery.end_hour) {
      this.inputValue.endHour = orderSearchQuery.end_hour;
    }
    if (orderSearchQuery.qualifications) {
      const qualifications = orderSearchQuery.qualifications.split(",");
      this.selectedQualifications = qualifications;
      this.inputValue.qualifications = qualifications;
    }
    if (orderSearchQuery.service_types) {
      const serviceTypes = orderSearchQuery.service_types.split(",");
      this.selectedServiceTypes = serviceTypes;
      this.inputValue.serviceTypes = serviceTypes;
    }
    if (orderSearchQuery.duration_types) {
      const durationType = orderSearchQuery.duration_types.split(",");
      this.inputValue.durationTypes = durationType;
    }
    if (orderSearchQuery.shift_types) {
      const shiftType = orderSearchQuery.shift_types.split(",");
      this.inputValue.shiftTypes = shiftType;
    }
    if (orderSearchQuery.free_word) {
      this.inputValue.freeWord = orderSearchQuery.free_word;
    }
    if (this.isLogin && this.isNursing && orderSearchQuery.only_nursing) {
      this.inputValue.isCheckedOnlyNursing = true;
    }
    if (orderSearchQuery.exclude_career) {
      this.inputValue.isCheckedExcludeCareer = true;
    }
    if (orderSearchQuery.hourly_wage) {
      this.inputValue.hourlyWage = orderSearchQuery.hourly_wage;
    }
    if (orderSearchQuery.recommendation_points) {
      const recommendationPoints = orderSearchQuery.recommendation_points.split(
        ","
      );
      this.inputValue.recommendationPoints = recommendationPoints;
    }
    if (orderSearchQuery.without_bath_assistance) {
      this.inputValue.withoutBathAssistance = true;
    }
    if (orderSearchQuery.with_shuttle_driver_duties) {
      this.inputValue.withShuttleDriverDuties = true;
    }
    if (orderSearchQuery.can_commute_by_car) {
      this.inputValue.canCommuteByCar = true;
    }
    if (orderSearchQuery.service_started_within_last_five_years) {
      this.inputValue.serviceStartedWithinLastFiveYears = true;
    }
  }
  async savePrefectureCondition() {
    // チェックを外した県の市区町村の選択を解除
    this.removePrefectureLocalityValues();
    this.selectedPrefectures = this.inputValue.prefectures;
    this.localityOptions = [];
    await Promise.all(
      this.selectedPrefectures.map(prefecture =>
        this.fetchLocalityOptions(prefecture)
      )
    );
    this.isOpenedPrefectureFilter = false;
  }
  saveServiceTypeCondition() {
    this.selectedServiceTypes = this.inputValue.serviceTypes;
    this.isOpenedServiceTypeFilter = false;
  }
  onChangeCanApply(v: boolean) {
    const qualifications = v ? this.myQualifications : [];
    this.selectedQualifications = qualifications;
    this.inputValue.qualifications = qualifications;
  }
  removePrefectureLocalityValues() {
    const removePrefectures = this.selectedPrefectures.filter(item => {
      return !this.inputValue.prefectures.includes(item);
    });

    const removeLocalityValues = this.localityOptions
      .filter(option => {
        return removePrefectures.includes(option.prefecture.id);
      })
      .map(item => {
        return item.localities.map(locality => {
          return locality.value;
        });
      })
      .flat();

    this.inputValue.localities = [...this.inputValue.localities].filter(
      locality => {
        return !removeLocalityValues.includes(locality);
      }
    );
  }
}
</script>

<style lang="scss" scoped>
.checkbox {
  display: inline-flex;
  align-items: center;

  :deep(span) {
    font-size: 14px !important;
  }
}

.background {
  height: 100vh;
}

.search-conditions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 100px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fix {
  position: fixed;
}

.condition-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 8px;
  background: $white;
  border: 1px solid $gray-03;
  border-radius: 4px;

  &.disabled {
    color: $gray-05;
    pointer-events: none;
    background: $gray-03;

    span {
      color: transparent;
    }
  }

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__icon {
    min-width: 16px;
  }

  &__count {
    min-width: 32px;
    color: $blue;
  }
}

.fixed-button {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 100;
  width: 100%;
  padding: 8px 16px 28px;
  background: $white;
  border-top: 1px solid $gray-03;
}

.checkbox.disabled {
  pointer-events: none;

  :deep(svg),
  :deep(span) {
    color: $gray-04;
  }
}

.freeword::placeholder {
  color: $gray-04;
}

.condition-headline {
  margin-bottom: 8px;
  font-size: 14px;
  color: $mild;
}

.work-time-checkbox {
  :deep(label) {
    margin-bottom: 4px !important;
    font-size: 12px !important;
    color: $gray-05 !important;
  }
}

.input-label {
  margin-bottom: 4px;
  font-size: 12px;
  color: $gray-05;
}

.reset-button {
  color: $primary;
}

.additional-condition-summary {
  position: relative;
  list-style: none;

  &-icon {
    position: absolute;
    right: 0;
    transition: transform 0.3s ease;
  }
}

details[open] .additional-condition-summary-icon {
  transform: rotate(90deg);
}

.count-details-condition {
  padding: 2px 8px;
  margin-left: 8px;
  color: $white;
  background-color: $primary;
  border-radius: 100%;
}

.locality-mock-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 8px;
  background: $gray-02;
  border: 1px solid $gray-03;
  border-radius: 4px;

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.operations {
  display: flex;
  flex-wrap: wrap;

  &-checkbox {
    flex-basis: 50%;
    padding-right: 8px;

    &:nth-child(2n) {
      padding-left: 8px;
    }

    &:nth-child(n + 3) {
      margin-top: 8px;
    }
  }
}
</style>
